import { useCallback, useEffect, useMemo, useState } from 'react';
import { cancelPolicy, dueAtGymLine, frontDeskNumber, goToPay, payHeldCta, payHeldLine, PAY_LAPSED_LINE, WHOLE_BOOKING_CODES, whatsappUrl, type BookingPay } from '../bookingPay';
import type { MemberGym } from '../hooks/useMemberGyms';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useMemberAuth } from '../../../contexts/MemberAuthContext';
import { mfetch, MemberApiError, MemberBooking, ExpiringCredit, AppointmentTimesData, CourtGridData, RescheduleResult } from '../api';
import Icon from '../design/Icon';
import { Sheet, fmtTime, fmtDateShort, todayStr, addDaysStr } from '../design/atoms';
import StandingSlots from '../components/StandingSlots';

/* SPEC 06 My bookings — upcoming · 07 past. */

const clock = (ms: number) => {
  const sec = Math.ceil(ms / 1000);
  return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;
};

/**
 * Time left on an unpaid booking's seat hold. Recomputed from the deadline on
 * every tick — never decremented — against the server's clock (the one that
 * releases the seat), via the offset taken when the list was read.
 */
function useHeldLeft(expiresAt: string | null, offsetMs: number): number | null {
  const [, tick] = useState(0);
  useEffect(() => {
    if (!expiresAt) return;
    const id = window.setInterval(() => tick(n => n + 1), 1000);
    return () => window.clearInterval(id);
  }, [expiresAt]);
  if (!expiresAt) return null;
  return Math.max(0, new Date(expiresAt).getTime() - (Date.now() + offsetMs));
}

interface OutletCtx {
  activeGymId: string | null;
  activeGym?: MemberGym | null;
}

interface PastSummary {
  attended: number;
  missed: number;
  attendancePercent: number | null;
  periodLabel: string;
}

export default function MemberBookingsPage() {
  const { token } = useMemberAuth();
  const { activeGymId, activeGym } = useOutletContext<OutletCtx>();
  const navigate = useNavigate();

  const [scope, setScope] = useState<'upcoming' | 'past'>('upcoming');
  const [rows, setRows] = useState<MemberBooking[]>([]);
  const [summary, setSummary] = useState<PastSummary | null>(null);
  const [credits, setCredits] = useState<ExpiringCredit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelTarget, setCancelTarget] = useState<MemberBooking | null>(null);
  const [reschedTarget, setReschedTarget] = useState<MemberBooking | null>(null);
  const [clockOffsetMs, setClockOffsetMs] = useState(0);
  const [payingId, setPayingId] = useState<string | null>(null);
  const [payError, setPayError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!token || !activeGymId) return;
    setLoading(true);
    setError(null);
    mfetch(token, '/member/bookings', { params: { gymId: activeGymId, scope } })
      .then(r => {
        setRows(r.data);
        setClockOffsetMs(r.now ? new Date(r.now).getTime() - Date.now() : 0);
        setSummary(r.summary ?? null);
        setCredits(r.expiringCredits ?? []);
      })
      .catch(e => setError(e instanceof MemberApiError ? e.message : 'Could not load bookings'))
      .finally(() => setLoading(false));
  }, [token, activeGymId, scope]);

  useEffect(load, [load]);

  // Paying for a booking left unpaid: the link covers the whole booking and is
  // the one already open for it. The pay page brings the member back here.
  const pay = async (b: MemberBooking) => {
    if (!token) return;
    setPayingId(b.id);
    setPayError(null);
    try {
      const r = await mfetch<{ data: { pay: BookingPay } }>(token, `/member/bookings/${b.id}/pay`, { method: 'POST', body: {} });
      goToPay(r.data.pay.url);
    } catch (e) {
      setPayingId(null);
      const code = e instanceof MemberApiError ? e.code : undefined;
      if (code === 'NOTHING_OWED') return load(); // paid meanwhile
      setPayError(code === 'PAYMENT_LAPSED' ? PAY_LAPSED_LINE
        : e instanceof MemberApiError ? e.message : 'Could not open the payment. Please try again.');
      if (code === 'PAYMENT_LAPSED') load();
    }
  };

  const today = todayStr();

  return (
    <div className="mpage">
      <div className="row" style={{ paddingTop: 8, gap: 10 }}>
        <button className="icon-btn" aria-label="Back" onClick={() => navigate(-1)}>
          <Icon n="left" s={18} c="var(--ink-2)" />
        </button>
        <div className="dp dp-l">My bookings</div>
      </div>

      <div className="row" style={{ gap: 8 }}>
        {(['upcoming', 'past'] as const).map(s => (
          <button key={s} className={`chip${scope === s ? ' on' : ''}`} onClick={() => setScope(s)}>
            {s === 'upcoming' ? 'Upcoming' : 'Past'}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="col" style={{ gap: 9 }}>
          {[96, 96, 96].map((h, i) => <div key={i} className="skel" style={{ height: h }} />)}
        </div>
      ) : error ? (
        <div className="card flat">
          <div className="sub wrap">{error}</div>
          <button className="btn ghost sm" style={{ marginTop: 10 }} onClick={load}>Retry</button>
        </div>
      ) : scope === 'upcoming' ? (
        <>
          {token && activeGymId && <StandingSlots token={token} gymId={activeGymId} onChanged={load} />}
          {rows.length > 0 && <div className="lbl">Booked</div>}
          {payError && (
            <div className="card flat" style={{ padding: '10px 12px', borderColor: 'var(--clay)', background: 'var(--clay-soft)' }}>
              <span className="sub wrap" style={{ color: 'var(--clay)' }}>{payError}</span>
            </div>
          )}
          {rows.length === 0 ? (
            <div className="card flat" style={{ textAlign: 'center', padding: 26 }}>
              <div className="mid">Nothing booked yet</div>
              <button className="btn gold sm" style={{ marginTop: 12 }} onClick={() => navigate('/app/book')}>
                Browse sessions
              </button>
            </div>
          ) : (
            rows.map(b => (
              <UpcomingCard
                key={b.id}
                b={b}
                today={today}
                onCancel={() => setCancelTarget(b)}
                onReschedule={() => setReschedTarget(b)}
                onCheckIn={() => navigate('/app/checkin')}
                clockOffsetMs={clockOffsetMs}
                paying={payingId === b.id}
                onPay={() => pay(b)}
              />
            ))
          )}
          {credits.map(c => (
            <div key={c.id} className="card dashed" style={{ padding: 14 }}>
              <div className="between">
                <div className="col" style={{ gap: 2, minWidth: 0 }}>
                  <div className="mid">{c.remainingCredits} pack credit{c.remainingCredits === 1 ? '' : 's'} expiring</div>
                  <div className="tiny">Use before {fmtDateShort(c.expiresAt.slice(0, 10))}</div>
                </div>
                <button className="btn gold sm" onClick={() => navigate('/app/book')}>Book</button>
              </div>
            </div>
          ))}
        </>
      ) : (
        <>
          {summary && (summary.attended > 0 || summary.missed > 0) && (
            <div className="card ink" style={{ padding: '18px 18px 16px' }}>
              <div className="lbl">Attendance</div>
              <div className="row" style={{ gap: 18, marginTop: 10 }}>
                <div className="col" style={{ gap: 2 }}>
                  <div className="num" style={{ fontSize: 22, color: 'var(--on-ink)' }}>{summary.attended}</div>
                  <div className="tiny">Attended</div>
                </div>
                <div className="col" style={{ gap: 2 }}>
                  <div className="num" style={{ fontSize: 22, color: 'var(--on-ink)' }}>{summary.missed}</div>
                  <div className="tiny">Missed</div>
                </div>
                {summary.attendancePercent !== null && (
                  <div className="col" style={{ gap: 2 }}>
                    <div className="num" style={{ fontSize: 22, color: 'var(--ink-accent)' }}>{summary.attendancePercent}%</div>
                    <div className="tiny">Attendance</div>
                  </div>
                )}
              </div>
              <div className="sub" style={{ marginTop: 10 }}>{summary.periodLabel}</div>
            </div>
          )}
          {rows.length === 0 ? (
            <div className="card flat" style={{ textAlign: 'center', padding: 26 }}>
              <div className="mid">No past sessions yet</div>
            </div>
          ) : (
            <div className="card" style={{ padding: '4px 16px' }}>
              {rows.map(b => (
                <div key={b.id} className="lrow">
                  <div className="col" style={{ width: 44, flex: '0 0 44px', alignItems: 'center' }}>
                    <div className="tiny">{fmtDateShort(b.session.date).split(' ')[0]}</div>
                    <div className="num" style={{ fontSize: 16 }}>{b.session.date.slice(8, 10)}</div>
                  </div>
                  <div className="col" style={{ gap: 1 }}>
                    <div className="mid trunc">{b.session.class.name}</div>
                    <div className="tiny trunc">
                      {[fmtTime(b.session.startTime), b.session.coach?.name].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  {(b.units ?? 1) > 1 && <span className="tag">{b.units} slots</span>}
                  <span className={`tag ${b.status === 'attended' ? 'sage' : b.status === 'no_show' ? 'clay' : ''}`}>
                    {b.status === 'attended' ? 'Attended' : b.status === 'no_show' ? 'Missed' : b.status}
                  </span>
                </div>
              ))}
            </div>
          )}
          {rows.length > 0 && <div className="tiny" style={{ textAlign: 'center' }}>Showing {summary?.periodLabel?.toLowerCase() ?? 'recent sessions'}</div>}
        </>
      )}

      {cancelTarget && (
        <CancelSheet
          booking={cancelTarget}
          policy={cancelPolicy(cancelTarget, rows)}
          gym={activeGym ?? null}
          token={token!}
          onClose={() => setCancelTarget(null)}
          onCancelled={() => {
            setCancelTarget(null);
            load();
          }}
        />
      )}

      {reschedTarget && activeGymId && (
        <RescheduleSheet
          booking={reschedTarget}
          gymId={activeGymId}
          token={token!}
          onClose={() => setReschedTarget(null)}
          onDone={() => {
            setReschedTarget(null);
            load();
          }}
        />
      )}
    </div>
  );
}

/** Bookable types whose sessions are appointment/grid-materialized and thus movable. */
const RESCHEDULABLE_TYPES = ['spa', 'pt', 'consult', 'facility'];

function UpcomingCard({
  b, today, onCancel, onReschedule, onCheckIn, clockOffsetMs, paying, onPay,
}: {
  b: MemberBooking;
  today: string;
  onCancel: () => void;
  onReschedule: () => void;
  onCheckIn: () => void;
  clockOffsetMs: number;
  paying: boolean;
  onPay: () => void;
}) {
  const d = new Date(`${b.session.date}T00:00:00`);
  const isToday = b.session.date === today;
  const waitlisted = b.status === 'waitlisted';
  // An online booking nobody has paid for: the seat is real but only held.
  // Only these cards tick — no timer runs for a null deadline.
  const owed = b.status === 'booked' ? b.pay ?? null : null;
  // A drop-in the gym collects for at the desk, not paid yet.
  const dueLine = b.status === 'booked' ? dueAtGymLine(b.amountDue) : null;
  const heldLeft = useHeldLeft(owed?.expiresAt ?? null, clockOffsetMs);
  const lapsed = heldLeft === 0;
  const movable = b.status === 'booked' && RESCHEDULABLE_TYPES.includes(b.session.class.bookableType);
  return (
    <div className="card" style={{ padding: 14 }}>
      <div className="row" style={{ gap: 13 }}>
        <div className="col" style={{ width: 52, flex: '0 0 52px', alignItems: 'center', borderRight: '1px solid var(--line)', paddingRight: 10 }}>
          <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '.1em', color: 'var(--ink-3)' }}>
            {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'][d.getDay()]}
          </div>
          <div className="num" style={{ fontSize: 20 }}>{d.getDate()}</div>
        </div>
        <div className="col" style={{ gap: 2, flex: 1, minWidth: 0 }}>
          <div className="mid trunc">{b.session.class.name}</div>
          <div className="tiny trunc">
            {[fmtTime(b.session.startTime), b.session.coach?.name, b.session.room?.name ?? b.session.branch?.displayName].filter(Boolean).join(' · ')}
          </div>
          <div className="row" style={{ gap: 6, marginTop: 3 }}>
            <span className={`tag ${waitlisted ? 'gold' : owed ? 'clay' : 'sage'}`}>
              {waitlisted ? `Waitlist${b.position ? ` #${b.position}` : ''}` : owed ? 'Unpaid' : 'Confirmed'}
            </span>
            {/* How it is covered. "Drop-in", not "Paid": a drop-in may not be paid yet — the line below says when it is still owed at the gym. */}
            {b.entitlement && <span className="tag">{b.entitlement === 'included' ? 'Included' : b.entitlement === 'pack' ? 'Pack' : 'Drop-in'}</span>}
            {!waitlisted && (b.units ?? 1) > 1 && <span className="tag">{b.units} slots</span>}
            {b.standingBookingId && <span className="tag gold">Weekly</span>}
          </div>
        </div>
      </div>
      {!waitlisted && (
        <>
          <div className="divider" style={{ margin: '12px 0 10px' }} />
          {dueLine && (
            <div className="row" style={{ gap: 8, marginBottom: 10 }}>
              <Icon n="card" s={15} c="var(--clay)" />
              <span className="sub num wrap" style={{ color: 'var(--clay)' }}>{dueLine}</span>
            </div>
          )}
          {owed && heldLeft != null && (
            <div className="row" style={{ gap: 8, marginBottom: 10 }} aria-live="polite">
              <Icon n="clock" s={15} c={lapsed ? 'var(--clay)' : 'var(--gold-2)'} />
              <span className="sub num wrap" style={lapsed ? { color: 'var(--clay)' } : undefined}>
                {lapsed ? PAY_LAPSED_LINE : payHeldLine(clock(heldLeft))}
              </span>
            </div>
          )}
          <div className="row" style={{ gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            {movable && <button className="btn ghost sm" onClick={onReschedule}>Reschedule</button>}
            <button className="btn ghost sm danger" onClick={onCancel}>Cancel</button>
            {isToday && <button className="btn gold sm" onClick={onCheckIn}>Check in</button>}
            {owed && !lapsed && (
              <button className="btn gold sm" disabled={paying} onClick={onPay}>
                {paying ? 'Opening payment…' : payHeldCta(owed.amount)}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function CancelSheet({
  booking, policy: initialPolicy, gym, token, onClose, onCancelled,
}: {
  booking: MemberBooking;
  /** Who cancels, and how many spots go together (bookingPay.cancelPolicy). */
  policy: { byGym: boolean; whole: boolean; spots: number };
  gym: MemberGym | null;
  token: string;
  onClose: () => void;
  onCancelled: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [policy, setPolicy] = useState(initialPolicy);
  const cutoff = booking.session.class.cancellationCutoffHours;
  const owes = !!booking.pay || (booking.amountDue ?? 0) > 0;
  const phone = frontDeskNumber(gym);
  const when = `${fmtDateShort(booking.session.date)} at ${fmtTime(booking.session.startTime)}`;
  const ask = `Hi, I'd like to cancel my booking for ${booking.session.class.name} on ${when}.`;

  const doCancel = async () => {
    setBusy(true);
    setError(null);
    try {
      await mfetch(token, `/member/bookings/${booking.id}${policy.whole ? '?scope=group' : ''}`, { method: 'DELETE', body: {} });
      setDone(true);
      setBusy(false);
    } catch (e) {
      setBusy(false);
      // The list was older than the booking: it holds more spots, or it was paid meanwhile.
      if (e instanceof MemberApiError && e.code && WHOLE_BOOKING_CODES.includes(e.code)) {
        setPolicy({ byGym: false, whole: true, spots: Number(e.body?.seats) || 2 });
        return;
      }
      if (e instanceof MemberApiError && e.code === 'CANCEL_AT_GYM') {
        setPolicy(p => ({ ...p, byGym: true }));
        return;
      }
      setError(e instanceof MemberApiError ? e.message : 'Could not cancel the booking.');
    }
  };

  // What happens to what it was booked on, so the member is not left guessing.
  const back = booking.entitlement === 'pack'
    ? (policy.whole ? 'The sessions go back to your pack.' : 'The session goes back to your pack.')
    : booking.entitlement === 'included'
      ? 'Your plan session is given back.'
      : owes ? 'Nothing is owed once it is cancelled.' : '';

  return (
    <Sheet onClose={busy ? () => {} : done ? onCancelled : onClose}>
      <div className="col" style={{ gap: 4 }}>
        <div className="dp dp-m" style={{ whiteSpace: 'normal' }}>
          {done
            ? 'Booking cancelled'
            : policy.byGym
              ? 'The gym cancels this one'
              : policy.whole ? `Cancel all ${policy.spots} spots?` : `Cancel ${booking.session.class.name}?`}
        </div>
        <div className="sub">{fmtDateShort(booking.session.date)} · {fmtTime(booking.session.startTime)}</div>
      </div>

      {done ? (
        <div className="card flat" style={{ padding: '10px 12px' }}>
          <span className="sub wrap">{back || 'You cancelled in time — nothing to pay.'}</span>
        </div>
      ) : policy.byGym ? (
        <div className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
          <Icon n="help" s={15} c="var(--ink-3)" />
          <span className="tiny wrap" style={{ lineHeight: 1.4 }}>
            This booking has been paid for, so the gym cancels it and arranges any refund. Contact the front desk.
          </span>
        </div>
      ) : (
        <div className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
          <Icon n="shield" s={15} c="var(--ink-3)" />
          <span className="tiny wrap" style={{ lineHeight: 1.4 }}>
            {[
              policy.whole ? `Its ${policy.spots} spots are cancelled together, not one at a time.` : '',
              back,
              `Cancellation closes ${cutoff} ${cutoff === 1 ? 'hr' : 'hrs'} before the session.`,
            ].filter(Boolean).join(' ')}
          </span>
        </div>
      )}

      {error && (
        <div className="card flat" style={{ padding: '10px 12px', borderColor: 'var(--clay)', background: 'var(--clay-soft)' }}>
          <span className="sub wrap" style={{ color: 'var(--clay)' }}>{error}</span>
        </div>
      )}

      {done ? (
        <button className="btn block" onClick={onCancelled}>Done</button>
      ) : policy.byGym ? (
        <div className="col" style={{ gap: 10 }}>
          {phone && (
            <>
              <a className="btn block" href={whatsappUrl(phone, ask)} target="_blank" rel="noreferrer">WhatsApp the gym</a>
              <a className="btn ghost block" href={`tel:${phone}`}>Call {phone}</a>
            </>
          )}
          <button className="btn ghost block" onClick={onClose}>Close</button>
        </div>
      ) : (
        <div className="col" style={{ gap: 10 }}>
          <button className="btn block" disabled={busy} onClick={doCancel} style={{ background: 'var(--clay)', color: 'var(--card)' }}>
            {busy ? 'Cancelling…' : policy.whole ? `Cancel all ${policy.spots}` : 'Cancel booking'}
          </button>
          <button className="btn ghost block" disabled={busy} onClick={onClose}>Keep booking</button>
        </div>
      )}
    </Sheet>
  );
}

// ── Reschedule sheet (appointment/grid bookings — Booking V2 phase 3) ────────

function toMin(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
}

function RescheduleSheet({
  booking, gymId, token, onClose, onDone,
}: {
  booking: MemberBooking;
  gymId: string;
  token: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const isFacility = booking.session.class.bookableType === 'facility';
  const units = Math.max(1, booking.units ?? 1);
  const cutoff = booking.session.class.cancellationCutoffHours;
  const today = todayStr();
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDaysStr(today, i)), [today]);
  const [date, setDate] = useState(days.includes(booking.session.date) ? booking.session.date : days[0]);

  const [appt, setAppt] = useState<AppointmentTimesData | null>(null);
  const [grid, setGrid] = useState<CourtGridData | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pick, setPick] = useState<{ startTime: string; roomId?: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [moved, setMoved] = useState<RescheduleResult | null>(null);

  const classId = booking.session.class.id;
  const coachId = booking.session.coach?.id;

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    setPick(null);
    if (isFacility) {
      mfetch(token, '/member/court-grid', { params: { gymId, classId, date, excludeBookingId: booking.id, branchId: booking.session.branch?.id } })
        .then(r => setGrid(r.data))
        .catch(e => setLoadError(e instanceof MemberApiError ? e.message : 'Could not load available times'))
        .finally(() => setLoading(false));
    } else {
      // Reschedule keeps the assigned provider — only ask for that staff's times.
      mfetch(token, '/member/appointment-times', { params: { gymId, classId, date, staffId: coachId, excludeBookingId: booking.id, branchId: booking.session.branch?.id } })
        .then(r => setAppt(r.data))
        .catch(e => setLoadError(e instanceof MemberApiError ? e.message : 'Could not load available times'))
        .finally(() => setLoading(false));
    }
  }, [token, gymId, classId, date, isFacility, coachId, booking.id, booking.session.branch?.id]);

  useEffect(load, [load]);

  // Court starts: for each court, times where `units` consecutive cells are free.
  // The booking's own cells count as free when looking at its current date+court,
  // since the server ignores the session's own row when moving it.
  const courtStarts = useMemo(() => {
    if (!isFacility || !grid) return [];
    return grid.courts
      .map(c => {
        // Server already treats the booking's own cells as free (excludeBookingId)
        const free = c.cells.map(cell => cell.available);
        const starts: string[] = [];
        for (let i = 0; i + units <= c.cells.length; i++) {
          let ok = true;
          for (let k = 0; k < units; k++) {
            if (!free[i + k] || toMin(c.cells[i + k].startTime) !== toMin(c.cells[i].startTime) + k * grid.cellMinutes) {
              ok = false;
              break;
            }
          }
          if (ok) starts.push(c.cells[i].startTime);
        }
        return { id: c.id, name: c.name, starts };
      })
      .filter(c => c.starts.length > 0);
  }, [isFacility, grid, date, booking, units]);

  const times = !isFacility && appt ? appt.times : [];
  const noTimes = isFacility ? courtStarts.length === 0 : times.length === 0;

  const confirm = async () => {
    if (!pick) return;
    setBusy(true);
    setError(null);
    const started = Date.now();
    try {
      const r = await mfetch(token, `/member/bookings/${booking.id}/reschedule`, {
        method: 'POST',
        body: { date, startTime: pick.startTime, ...(pick.roomId ? { roomId: pick.roomId } : {}) },
      });
      // Keep a 400ms minimum so the busy state reads as deliberate (MOTION.md)
      const wait = Math.max(0, 400 - (Date.now() - started));
      setTimeout(() => {
        setMoved(r.data);
        setBusy(false);
      }, wait);
    } catch (e) {
      setBusy(false);
      if (e instanceof MemberApiError) {
        setError(e.code === 'WRONG_MODE'
          ? "This booking can't be rescheduled — cancel and rebook instead."
          : e.message);
      } else {
        setError('Could not move the booking. Please try again.');
      }
    }
  };

  return (
    <Sheet onClose={busy ? () => {} : moved ? onDone : onClose}>
      <div className="col" style={{ gap: 4 }}>
        <div className="dp dp-m" style={{ whiteSpace: 'normal' }}>
          {moved ? 'Booking moved' : `Move ${booking.session.class.name}`}
        </div>
        <div className="sub">{fmtDateShort(booking.session.date)} · {fmtTime(booking.session.startTime)}</div>
      </div>

      {moved ? (
        <>
          <div className="card flat" style={{ padding: '10px 12px' }}>
            <span className="sub wrap">
              Moved to {fmtDateShort(moved.session.date)} · {fmtTime(moved.session.startTime)}
              {moved.session.roomName ? ` · ${moved.session.roomName}` : ''}.
            </span>
          </div>
          <button className="btn block" onClick={onDone}>Done</button>
        </>
      ) : (
        <>
          {/* 7-day date strip */}
          <div className="hscroll">
            {days.map(d => {
              const label = d === today ? 'Today' : fmtDateShort(d).split(' ').slice(0, 2).join(' ');
              return (
                <button key={d} className={`chip${date === d ? ' on' : ''}`} disabled={busy} onClick={() => setDate(d)}>
                  {label}
                </button>
              );
            })}
          </div>

          {loading ? (
            <div className="col" style={{ gap: 9 }}>
              {[36, 36].map((h, i) => <div key={i} className="skel" style={{ height: h }} />)}
            </div>
          ) : loadError ? (
            <div className="card flat">
              <div className="sub wrap">{loadError}</div>
              <button className="btn ghost sm" style={{ marginTop: 10 }} onClick={load}>Retry</button>
            </div>
          ) : noTimes ? (
            <div className="card flat" style={{ textAlign: 'center', padding: 22 }}>
              <div className="mid">No times on this date</div>
              <div className="tiny wrap" style={{ marginTop: 4 }}>Try another date from the strip above.</div>
            </div>
          ) : isFacility ? (
            <div className="col" style={{ gap: 10 }}>
              {courtStarts.map(c => (
                <div key={c.id} className="col" style={{ gap: 8 }}>
                  <div className="lbl">{c.name}</div>
                  <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                    {c.starts.map(t => {
                      const on = pick?.startTime === t && pick?.roomId === c.id;
                      return (
                        <button
                          key={t}
                          className={`chip${on ? ' on' : ''}`}
                          disabled={busy}
                          onClick={() => setPick({ startTime: t, roomId: c.id })}
                        >
                          {fmtTime(t)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
              {times.map(t => (
                <button
                  key={t.startTime}
                  className={`chip${pick?.startTime === t.startTime ? ' on' : ''}`}
                  disabled={busy}
                  onClick={() => setPick({ startTime: t.startTime })}
                >
                  {fmtTime(t.startTime)}
                </button>
              ))}
            </div>
          )}

          <div className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
            <Icon n="shield" s={15} c="var(--ink-3)" />
            <span className="tiny wrap" style={{ lineHeight: 1.4 }}>
              Free rescheduling until {cutoff} {cutoff === 1 ? 'hr' : 'hrs'} before your booking.
            </span>
          </div>

          {error && (
            <div className="card flat" style={{ padding: '10px 12px', borderColor: 'var(--clay)', background: 'var(--clay-soft)' }}>
              <span className="sub wrap" style={{ color: 'var(--clay)' }}>{error}</span>
            </div>
          )}

          <button className="btn gold block" disabled={busy || !pick} onClick={confirm}>
            {busy ? 'Moving…' : 'Move booking'}
          </button>
        </>
      )}
    </Sheet>
  );
}
